import { useEffect, useCallback } from 'react';
import { useDataProvider, useEvent } from 'react-admin';
/**
 * Subscribe to the events from a topic on mount (and unsubscribe on unmount).
 *
 * @param {string} topic The topic to subscribe to
 * @param {SubscriptionCallback<RecordEvent>} callback The callback to call when an event is received on the topic
 * @param {UseSubscribeOptions} options Options object
 * @param options.enabled Whether to subscribe or not. Defaults to true
 * @param options.once Whether to unsubscribe after the first event. Defaults to false
 * @param options.unsubscribeOnUnmount Whether to unsubscribe on unmount. Defaults to true
 *
 * @example
 * import { useState } from 'react';
 * import { Badge, Typography } from '@mui/material';
 * import { useSubscribe } from '@react-admin/ra-realtime';
 *
 * const ChannelName = ({ name }) => {
 *     const [nbMessages, setNbMessages] = useState(0);
 *     useSubscribe(`messages/${name}`, (event) => {
 *         if(event.type === 'created') {
 *             setNbMessages(count => count + 1);
 *         }
 *     });
 *     return nbMessages > 0 ? (
 *         <Badge badgeContent={nbMessages} color="primary">
 *             <Typography># {name}</Typography>
 *         </Badge>
 *     ) : (
 *        <Typography># {name}</Typography>
 *     );
 * };
 *
 * @example // unsubscribe when receiving a specific event
 * import { useState, useCallback } from 'react';
 * import { LinearProgress } from '@mui/material';
 * import { useSubscribe } from '@react-admin/ra-realtime';
 *
 * const JobProgress = ({ jobId }) => {
 *     const [progress, setProgress] = useState(0);
 *     const [color, setColor] = useState('primary');
 *     const callback = useCallback(
 *         (event, unsubscribe) => {
 *             if (event.type === 'progress') {
 *                 setProgress(event.payload.progress);
 *             }
 *             if (event.type === 'completed') {
 *                 setColor('success');
 *                 unsubscribe();
 *             }
 *         },
 *         [setColor]
 *     );
 *     useSubscribe(`jobs/${jobId}`, callback);
 *     return (
 *         <LinearProgress
 *             variant="determinate"
 *             value={progress}
 *             color={color}
 *         />
 *     );
 * };
 *
 * @example // subscribe once
 * import { useState } from 'react';
 * import { useSubscribe } from '@react-admin/ra-realtime';
 *
 * const OfficeClosed = () => {
 *     const [state, setState] = useState('closed');
 *
 *     useSubscribe('office/restart', () => setState('open'), { once: true });
 *
 *     return (
 *         <div>
 *             {state === 'closed'
 *                 ? 'Sorry, the office is closed for maintenance.'
 *                 : 'Welcome! The office is open.'}
 *         </div>
 *     );
 * };
 */
export var useSubscribe = function (topic, callback, options) {
    var dataProvider = useDataProvider();
    var _a = options || {}, _b = _a.once, once = _b === void 0 ? false : _b, _c = _a.enabled, enabled = _c === void 0 ? true : _c, _d = _a.unsubscribeOnUnmount, unsubscribeOnUnmount = _d === void 0 ? true : _d;
    var stableCallback = useEvent(callback);
    var callbackWithUnsubscribe = useCallback(function (event) {
        if (!topic)
            return;
        var unsubscribe = function () {
            return dataProvider.unsubscribe(topic, callbackWithUnsubscribe);
        };
        stableCallback(event, unsubscribe);
        if (once) {
            unsubscribe();
        }
    }, [stableCallback, dataProvider, topic, once]);
    useEffect(function () {
        if (!enabled || !dataProvider || !topic || !stableCallback) {
            return;
        }
        dataProvider.subscribe(topic, callbackWithUnsubscribe);
        return function () {
            unsubscribeOnUnmount &&
                dataProvider.unsubscribe(topic, callbackWithUnsubscribe);
        };
    }, [
        enabled,
        dataProvider,
        topic,
        stableCallback,
        callbackWithUnsubscribe,
        unsubscribeOnUnmount,
    ]);
};
